import React, { createContext, useContext, FC, ReactNode } from "react";

/**
 * Identity for an anonymous (guest) GIS session, sourced from the signed token
 * minted by the order-lookup funnel (Public_Entry_Order_Lookup). When `token`
 * is set, the shared intake/schedule components route their GraphQL through the
 * NonAuth resolvers instead of the authenticated (`ctx.user`) ones.
 *
 * The default value is an empty object so authenticated pages — which never
 * mount a provider — read `token === undefined` and keep their existing
 * behavior unchanged.
 */
export interface GisGuestValue {
  /** Signed guest token authorizing the NonAuth GIS/Acuity resolvers. */
  token?: string;
  /** Matched order's test type (e.g. "SIGNATERA"); drives Acuity routing. */
  testType?: string | null;
  /** Matched order's status; drives pre/post-test counseling intent. */
  status?: string | null;
  /** Counseling intent derived when the order is matched. */
  counselingIntent?: "pre-test" | "post-test" | null;
  /** Matched test's LIMS case ID; prefills the Acuity Case ID field. */
  limsCaseId?: number | null;
}

const GisGuestContext = createContext<GisGuestValue>({});

GisGuestContext.displayName = "GisGuestContext";

interface GisGuestProviderProps extends GisGuestValue {
  children: ReactNode;
}

export const GisGuestProvider: FC<GisGuestProviderProps> = ({
  children,
  token,
  testType,
  status,
  counselingIntent,
  limsCaseId,
}) => (
  <GisGuestContext.Provider
    value={{ token, testType, status, counselingIntent, limsCaseId }}
  >
    {children}
  </GisGuestContext.Provider>
);

/**
 * Returns the active guest identity, or an empty object (`token === undefined`)
 * when rendered in the authenticated flow with no provider.
 */
export const useGisGuest = (): GisGuestValue => useContext(GisGuestContext);

// ---------------------------------------------------------------------------
// sessionStorage bridge
//
// The guest funnel mints the token in one route (the lookup funnel) and then
// navigates to the intake/schedule routes, which mount a fresh component tree.
// We stash the token + matched-order meta in sessionStorage (scoped per
// order/test) so it survives the in-app navigations across intake steps.
// Reloading clears the lookup funnel's in-memory state anyway, so a missing
// entry simply routes the guest back to the lookup start.
// ---------------------------------------------------------------------------

/**
 * Cache key for the shared `IntakeFormProvider` state bucket used by a guest
 * intake attempt (scoped per order + test). Kept here so the guest layout and
 * the lookup funnel derive the exact same key — the funnel clears the bucket
 * before a fresh attempt and the layout mounts the provider against it.
 */
export function guestIntakeStateCacheKey(
  orderUidPublic: string,
  testUid: string
): string {
  return `gis-guest:${orderUidPublic}:${testUid}`;
}

const GUEST_SESSION_PREFIX = "gis_guest_session:";

export interface StoredGuestSession {
  token: string;
  testType?: string | null;
  status?: string | null;
  counselingIntent?: "pre-test" | "post-test" | null;
  limsCaseId?: number | null;
}

function guestSessionKey(orderUidPublic: string, testUid: string): string {
  return `${GUEST_SESSION_PREFIX}${orderUidPublic}:${testUid}`;
}

export function writeGuestGisSession(
  orderUidPublic: string,
  testUid: string,
  session: StoredGuestSession
): void {
  try {
    sessionStorage.setItem(
      guestSessionKey(orderUidPublic, testUid),
      JSON.stringify(session)
    );
  } catch {
    // Ignore quota / unavailable sessionStorage.
  }
}

export function readGuestGisSession(
  orderUidPublic: string,
  testUid: string
): StoredGuestSession | null {
  try {
    const raw = sessionStorage.getItem(
      guestSessionKey(orderUidPublic, testUid)
    );
    if (raw) return JSON.parse(raw) as StoredGuestSession;
  } catch {
    // Ignore parse / unavailable errors.
  }
  return null;
}

export function clearGuestGisSession(
  orderUidPublic: string,
  testUid: string
): void {
  try {
    sessionStorage.removeItem(guestSessionKey(orderUidPublic, testUid));
  } catch {
    // Ignore.
  }
}

// ---------------------------------------------------------------------------
// No-order lookup prefill bridge
//
// The no-order guest flow has no matched order (and often no matched patient)
// to prefill the confirm-info form from server-side. To avoid asking the
// visitor to retype what they just entered in the order-lookup funnel, the
// funnel stashes the details they typed (name / DOB / mobile / email) here just
// before navigating into the intake, and the no-order confirm-info step reads
// it as the top-priority prefill.
// ---------------------------------------------------------------------------

const GUEST_LOOKUP_PREFILL_KEY = "gis_guest_lookup_prefill";

export interface StoredGuestLookupPrefill {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
  lookupIdentifier?: "zipCode" | "mobileNumber" | "email";
}

export function writeGuestLookupPrefill(
  prefill: StoredGuestLookupPrefill
): void {
  try {
    const existing = readGuestLookupPrefill() ?? {};
    const definedPrefill = Object.fromEntries(
      Object.entries(prefill).filter(([, value]) => value !== undefined)
    );
    sessionStorage.setItem(
      GUEST_LOOKUP_PREFILL_KEY,
      JSON.stringify({ ...existing, ...definedPrefill })
    );
  } catch {
    // Ignore quota / unavailable sessionStorage.
  }
}

export function readGuestLookupPrefill(): StoredGuestLookupPrefill | null {
  try {
    const raw = sessionStorage.getItem(GUEST_LOOKUP_PREFILL_KEY);
    if (raw) return JSON.parse(raw) as StoredGuestLookupPrefill;
  } catch {
    // Ignore parse / unavailable errors.
  }
  return null;
}

export function clearGuestLookupPrefill(): void {
  try {
    sessionStorage.removeItem(GUEST_LOOKUP_PREFILL_KEY);
  } catch {
    // Ignore.
  }
}

export default GisGuestContext;
